import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, Lock, Unlock, Pencil, Save, Trash2, ExternalLink, Mail, Phone, FileSpreadsheet, User, MessageSquare, Activity as ActivityIcon, Vault, Info, Share2, Copy, XCircle, Plus, Eye, Archive, Globe, FileDown, Loader2 } from "lucide-react";
import { getInvestor, updateInvestor, deleteInvestor, setSperre, listExposeLinks, createExposeLink, deleteExposeLink, archiveExposeLink, exposePublicUrl, generateExposePdf, exposePdfDownloadUrl } from "../../lib/api";
import { PHASES, BETREUER, PhaseBadge, QuellenPill, BetreuerPill, EINSTUFUNGEN, EinstufungBadge, TierBadge, AffinitaetDot } from "./Badges";
import { fmtDate } from "../../lib/format";
import DocsTab from "./DocsTab";
import KontakthistorieTab from "./KontakthistorieTab";
import VerlaufTab from "./VerlaufTab";
import DatenraumFreigabeTab from "./DatenraumFreigabeTab";
import AnsprechpartnerTab from "./AnsprechpartnerTab";
import ErrorBoundary from "../ErrorBoundary";

const ABSAGE_GRUENDE = [
  "",
  "Investment vehicle passt nicht",
  "Standort",
  "Leasehold",
  "Sonstiges",
];

const BNP_SUB_CODES = ["", "7", "5.2", "5.1", "5.0", "4.1", "4.0", "3", "2", "1", "6.0", "6.1", "6.2", "6.3"];

// Zweispaltige Sperre-Box
const SperreSection = ({ data, busyKey, onToggle, onChangeGrund, grundInput, showGrundFor }) => (
  <section data-testid="drawer-sperre-section">
    <div className="pb-eyebrow mb-3">Sperren</div>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      {/* BNP-Richtung */}
      <div className="pb-card flex flex-col gap-3" data-testid="sperre-card-bnp">
        <div>
          <div className="pb-eyebrow text-[10px]">BNP darf NICHT ansprechen</div>
          <div className={`text-sm font-sans mt-1 ${data.gesperrt_fuer_bnp ? "text-gold font-medium" : "text-navy/55"}`}>
            {data.gesperrt_fuer_bnp ? "Gesperrt" : "Nicht gesperrt"}
          </div>
        </div>
        {showGrundFor === "bnp" && (
          <input
            data-testid="drawer-lock-grund-input-bnp"
            className="pb-input--full"
            placeholder="Grund (z. B. Heinrichs Liste)"
            value={grundInput}
            onChange={(e) => onChangeGrund(e.target.value)}
          />
        )}
        <button
          data-testid="drawer-toggle-sperre-bnp"
          disabled={busyKey === "bnp"}
          onClick={() => onToggle("bnp")}
          className={data.gesperrt_fuer_bnp ? "pb-btn-outline gap-2" : "pb-btn-primary gap-2"}
        >
          {data.gesperrt_fuer_bnp ? <><Unlock size={13}/> Entsperren</> : <><Lock size={13}/> Sperren</>}
        </button>
      </div>

      {/* Uns-Richtung */}
      <div className="pb-card flex flex-col gap-3" data-testid="sperre-card-uns">
        <div>
          <div className="pb-eyebrow text-[10px]">Wir dürfen NICHT ansprechen</div>
          <div className={`text-sm font-sans mt-1 ${data.gesperrt_fuer_uns ? "text-gold font-medium" : "text-navy/55"}`}>
            {data.gesperrt_fuer_uns ? "Gesperrt" : "Nicht gesperrt"}
          </div>
        </div>
        {showGrundFor === "uns" && (
          <input
            data-testid="drawer-lock-grund-input-uns"
            className="pb-input--full"
            placeholder="Grund (z. B. BNP-Exklusiv-Kontakt)"
            value={grundInput}
            onChange={(e) => onChangeGrund(e.target.value)}
          />
        )}
        <button
          data-testid="drawer-toggle-sperre-uns"
          disabled={busyKey === "uns"}
          onClick={() => onToggle("uns")}
          className={data.gesperrt_fuer_uns ? "pb-btn-outline gap-2" : "pb-btn-primary gap-2"}
        >
          {data.gesperrt_fuer_uns ? <><Unlock size={13}/> Entsperren</> : <><Lock size={13}/> Sperren</>}
        </button>
      </div>
    </div>
    {data.gesperrt_grund && (
      <div className="text-xs text-navy/60 mt-2 font-sans">
        Grund: <span className="text-navy">{data.gesperrt_grund}</span>
      </div>
    )}
    {data.bnp_sperre_aufgehoben_am && !data.gesperrt_fuer_uns && (
      <div
        data-testid="drawer-bnp-sperre-aufgehoben-info"
        className="mt-3 border-l-[3px] border-l-gold bg-gold/5 px-4 py-2.5 text-xs text-navy font-sans flex items-start gap-2"
      >
        <Info size={13} className="mt-0.5 text-gold shrink-0" />
        <span>
          <strong className="text-navy">INFO:</strong> Investor stand auf BNP-Exklusivliste,
          freigegeben durch {data.bnp_sperre_aufgehoben_von || "Heinrichs"}
          {data.bnp_sperre_aufgehoben_am && ` am ${new Date(data.bnp_sperre_aufgehoben_am).toLocaleDateString("de-DE")}`}.
          {data.kommentar?.includes("BNP-EXKLUSIVLISTE-HINWEIS") && " Begründung siehe Kommentar."}
        </span>
      </div>
    )}
  </section>
);

function InvestorDrawerInner({ investorId, open, onClose, onChange, initialTab }) {
  const [data, setData] = useState(null);
  const [orig, setOrig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showGrundFor, setShowGrundFor] = useState(null); // "bnp" | "uns" | null
  const [grundInput, setGrundInput] = useState("");
  const [busyKey, setBusyKey] = useState(null);
  // initialTab values: "info" | "docs" | "datenraum" | "kontakthistorie" | "verlauf"
  const _normalize = (t) => (t === "datenraum" ? "dr-freigabe" : t);
  const [activeTab, setActiveTab] = useState(_normalize(initialTab) || "info");
  const [mailtoPreview, setMailtoPreview] = useState(null);

  // Allow parent to switch tab after open (e.g. /datenraum → Open Investor → Datenraum-Tab)
  useEffect(() => {
    if (initialTab) setActiveTab(_normalize(initialTab));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialTab, investorId]);

  useEffect(() => {
    if (!investorId) return;
    setLoading(true);
    getInvestor(investorId)
      .then((d) => { setData(d); setOrig(d); })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error(`[InvestorDrawer] getInvestor(${investorId}) failed`, err, {
          status: err?.response?.status,
          detail: err?.response?.data,
        });
        toast.error("Investor konnte nicht geladen werden");
      })
      .finally(() => setLoading(false));
  }, [investorId]);

  if (!investorId) return null;
  const set = (k) => (v) => setData((d) => ({ ...d, [k]: v }));

  const handleMailto = async (typ) => {
    try {
      const { fetchMailtoDraft } = await import("../../lib/api");
      const draft = await fetchMailtoDraft(data.id, typ);
      setMailtoPreview({ ...draft, typ });
    } catch { toast.error("Mail-Vorlage konnte nicht geladen werden"); }
  };

  const handleSave = async () => {
    if (!data) return;
    setSaving(true);
    try {
      const payload = {
        // Identität / Adresse
        firma: data.firma, url: data.url, subline: data.subline,
        stadt: data.stadt, plz: data.plz, bundesland: data.bundesland, adresse: data.adresse,
        land: data.land, region_kurz: data.region_kurz,
        email: data.email, telefon: data.telefon,
        // Klassifikation
        tier: data.tier,
        welle: data.welle,
        einstufung: data.einstufung,
        phase: data.phase, bnp_sub_code: data.bnp_sub_code,
        betreuer_intern: data.betreuer_intern,
        investorentyp_kategorie: data.investorentyp_kategorie,
        investorentyp_subtyp: data.investorentyp_subtyp,
        // Affinitäten (Phase 5.3)
        ebr_affinitaet: data.ebr_affinitaet,
        ls_affinitaet: data.ls_affinitaet,
        de_affinitaet: data.de_affinitaet,
        // NDA / Status
        nda_status: data.nda_status, nda_versandt_am: data.nda_versandt_am, nda_unterzeichnet_am: data.nda_unterzeichnet_am,
        absage_grund: data.absage_grund,
        // Text-Felder
        kommentar: data.kommentar,
        schluessel_argument: data.schluessel_argument,
        letzter_kontakt_kontext: data.letzter_kontakt_kontext,
        // AUM / Fokus
        aum_mio_eur: data.aum_mio_eur, aum_text: data.aum_text,
        fokus_kategorien: data.fokus_kategorien,
        fokus_immobilien_details: data.fokus_immobilien_details,
        fokus_sonstiges: data.fokus_sonstiges,
        geschaeftsfuehrung_raw: data.geschaeftsfuehrung_raw,
      };
      const updated = await updateInvestor(data.id, payload);
      setData(updated); setOrig(updated);
      setEditMode(false);
      toast.success("Investor gespeichert.");
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Investor "${data.firma}" wirklich löschen?`)) return;
    try {
      await deleteInvestor(data.id);
      toast.success("Investor gelöscht."); onChange?.(); onClose?.();
    } catch { toast.error("Löschen fehlgeschlagen"); }
  };

  const toggleSperre = async (richtung) => {
    if (!data) return;
    const field = richtung === "bnp" ? "gesperrt_fuer_bnp" : "gesperrt_fuer_uns";
    const wantsLock = !data[field];
    // Grund-Input beim ersten Sperren-Klick immer einblenden (auch wenn schon ein anderer Grund existiert),
    // damit Heinrich sehen kann was eingegeben wird, bevor der zweite Klick persistiert.
    if (wantsLock && showGrundFor !== richtung) {
      setShowGrundFor(richtung);
      setGrundInput(data.gesperrt_grund || "");
      return;
    }
    setBusyKey(richtung);
    try {
      const updated = await setSperre(data.id, richtung, wantsLock, wantsLock ? (grundInput || data.gesperrt_grund) : null);
      setData(updated); setOrig(updated);
      setShowGrundFor(null); setGrundInput("");
      toast.success(wantsLock ? "Investor gesperrt." : "Sperre entfernt.");
      onChange?.();
    } catch {
      toast.error("Aktion fehlgeschlagen");
    } finally { setBusyKey(null); }
  };

  // Sofort-Speichern für einzelne Felder (Phase, BNP-Sub-Code etc.) ausserhalb des Edit-Modus.
  // Optimistic: lokal sofort setzen, Backend-Call im Hintergrund, bei Fehler Rollback.
  const quickUpdate = async (field, value) => {
    if (!data) return;
    const oldVal = data[field];
    if (oldVal === value) return;
    setData((d) => ({ ...d, [field]: value }));
    setBusyKey(field);
    try {
      const updated = await updateInvestor(data.id, { [field]: value });
      setData(updated); setOrig(updated);
      const label = { phase: "Phase", bnp_sub_code: "BNP-Sub-Code", nda_status: "NDA-Status",
                       betreuer_intern: "Betreuer", absage_grund: "Absage-Grund" }[field] || field;
      toast.success(`${label} gespeichert.`);
      onChange?.();
    } catch (e) {
      // Rollback
      setData((d) => ({ ...d, [field]: oldVal }));
      toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally { setBusyKey(null); }
  };

  return (
    <>
      <div data-testid="drawer-backdrop" onClick={onClose}
        className="fixed inset-0 bg-navy/30 z-40 animate-fade-in-up" style={{animationDuration:"0.15s"}} />
      <aside
        data-testid="investor-drawer"
        className="fixed top-0 right-0 h-full w-full sm:w-[min(640px,100vw)] max-w-[640px] bg-white border-l border-navy/15 z-50 flex flex-col"
        style={{ animation: "slide-in 0.2s ease-out" }}
      >
        <div className="flex items-center justify-between border-b border-navy/10 px-5 py-4 shrink-0">
          <div className="pb-eyebrow">Investor</div>
          <button data-testid="drawer-close" onClick={onClose} aria-label="Drawer schließen" className="text-navy/60 hover:text-navy min-w-[44px] min-h-[44px] -mr-2.5 inline-flex items-center justify-center">
            <X size={20} strokeWidth={1.5} />
          </button>
        </div>

        {loading || !data ? (
          <div className="p-8 text-navy/55 font-sans">Lade …</div>
        ) : (
          <div className="flex-1 overflow-y-auto px-5 py-5 space-y-7">
            {/* Header */}
            <div>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  {!editMode ? (
                    <h2 data-testid="drawer-firma" className="font-serif text-2xl text-navy leading-tight break-words">
                      {data.firma}
                      {(data.gesperrt_fuer_bnp || data.gesperrt_fuer_uns) && (
                        <Lock size={16} className="inline ml-2 text-gold" strokeWidth={1.8} />
                      )}
                    </h2>
                  ) : (
                    <input data-testid="drawer-firma-input" className="pb-input--full font-serif text-xl text-navy"
                      value={data.firma || ""} onChange={(e) => set("firma")(e.target.value)} />
                  )}
                  {data.stadt && (
                    <p className="text-navy/65 text-xs mt-1.5 font-sans">
                      {`${data.stadt}${data.bundesland ? ", " + data.bundesland : ""}${data.land ? " · " + data.land : ""}`}
                    </p>
                  )}
                </div>
                {!editMode ? (
                  <button data-testid="drawer-edit-btn" onClick={() => setEditMode(true)} className="pb-btn-outline gap-2 shrink-0">
                    <Pencil size={13} /> Bearbeiten
                  </button>
                ) : (
                  <div className="flex gap-2 shrink-0">
                    <button data-testid="drawer-cancel-btn" onClick={() => { setData(orig); setEditMode(false); }} className="pb-btn-ghost" disabled={saving}>Abbrechen</button>
                    <button data-testid="drawer-save-btn" onClick={handleSave} className="pb-btn-primary gap-2" disabled={saving}>
                      <Save size={13} /> {saving ? "…" : "Speichern"}
                    </button>
                  </div>
                )}
              </div>

              <div className="flex flex-wrap gap-1.5 mt-3" data-testid="drawer-quellen-pills">
                <BetreuerPill b={data.betreuer_intern || "Heinrichs"} testid="drawer-betreuer-pill" />
                {(data.quellen || []).map((q) => <QuellenPill key={q} q={q} />)}
                {data.auch_bei_bnp && (
                  <span className="text-[10px] uppercase tracking-widish px-2 py-0.5 border border-gold text-gold/90 font-sans">
                    Auch bei BNP
                  </span>
                )}
              </div>
            </div>

            {/* BNP-Exklusiv-Warning — sichtbarer Banner, falls Investor BNP exklusiv vorbehalten ist */}
            {data.gesperrt_fuer_uns && (data.gesperrt_grund || "").includes("BNP-Exklusiv") && (
              <div
                data-testid="drawer-bnp-exklusiv-banner"
                className="border-l-2 border-tot bg-tot/5 px-4 py-3 flex items-start gap-3"
              >
                <Lock size={18} className="text-tot shrink-0 mt-0.5" strokeWidth={2} />
                <div className="text-xs font-sans text-navy">
                  <div className="pb-eyebrow text-tot mb-1">⚠ BNP-Exklusivkontakt</div>
                  <p className="text-navy/85">
                    Bitte <strong>nicht direkt ansprechen</strong> — dieser Investor ist exklusiv
                    BNP Paribas vorbehalten. Stand: {(data.gesperrt_grund || "").replace(/^.*Stand /, "")}.
                  </p>
                </div>
              </div>
            )}

            {/* Direkt-Ansprache-Button für Tier 1-3 */}
            {[1, 2, 3].includes(data.tier) && (
              <div data-testid="drawer-direkt-ansprache-wrap" className="border-l-2 border-gold bg-ledger/40 px-4 py-3 flex items-start justify-between gap-4">
                <div className="text-xs font-sans text-navy/70 max-w-md">
                  <div className="pb-eyebrow mb-1">Tier-{data.tier} Match</div>
                  <p>
                    Nutzen Sie das hinterlegte Schlüssel-Argument für eine personalisierte Erstansprache.
                    Die Vorlage füllt sich automatisch.
                  </p>
                </div>
                <button
                  data-testid="drawer-direkt-ansprache-btn"
                  onClick={() => handleMailto("direkt_ansprache")}
                  disabled={!data.schluessel_argument}
                  title={!data.schluessel_argument
                    ? "Schlüssel-Argument fehlt — bitte erst im Match-Profil ergänzen"
                    : "Direkt-Ansprache-Mail vorbereiten"}
                  className="pb-btn-accent text-xs uppercase tracking-widish font-semibold gap-2 disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  <Mail size={13}/> Direkt-Ansprache vorbereiten
                </button>
              </div>
            )}

            {/* Tab-Switcher */}
            <div className="flex border-b border-navy/15 -mx-5 px-5" data-testid="drawer-tabs">
              <button
                onClick={() => setActiveTab("info")}
                data-testid="drawer-tab-info"
                className={`text-xs uppercase tracking-widish font-sans px-4 py-2 border-b-2 transition-colors duration-150 inline-flex items-center gap-1.5 ${
                  activeTab === "info" ? "border-gold text-gold font-medium" : "border-transparent text-navy/55 hover:text-navy"
                }`}
              >
                <User size={12}/> Stammdaten &amp; Sperre
              </button>
              <button
                onClick={() => setActiveTab("docs")}
                data-testid="drawer-tab-docs"
                className={`text-xs uppercase tracking-widish font-sans px-4 py-2 border-b-2 transition-colors duration-150 inline-flex items-center gap-1.5 ${
                  activeTab === "docs" ? "border-gold text-gold font-medium" : "border-transparent text-navy/55 hover:text-navy"
                }`}
              >
                <FileSpreadsheet size={12}/> Dokumente &amp; Datenraum
              </button>
              <button
                onClick={() => setActiveTab("dr-freigabe")}
                data-testid="drawer-tab-dr-freigabe"
                className={`text-xs uppercase tracking-widish font-sans px-4 py-2 border-b-2 transition-colors duration-150 inline-flex items-center gap-1.5 ${
                  activeTab === "dr-freigabe" ? "border-gold text-gold font-medium" : "border-transparent text-navy/55 hover:text-navy"
                }`}
              >
                <Vault size={12}/> Datenraum-Freigabe
              </button>
              <button
                onClick={() => setActiveTab("expose-link")}
                data-testid="drawer-tab-expose-link"
                className={`text-xs uppercase tracking-widish font-sans px-4 py-2 border-b-2 transition-colors duration-150 inline-flex items-center gap-1.5 ${
                  activeTab === "expose-link" ? "border-gold text-gold font-medium" : "border-transparent text-navy/55 hover:text-navy"
                }`}
              >
                <Share2 size={12}/> Exposé-Link
              </button>
              <button
                onClick={() => setActiveTab("ansprechpartner")}
                data-testid="drawer-tab-ansprechpartner"
                className={`text-xs uppercase tracking-widish font-sans px-4 py-2 border-b-2 transition-colors duration-150 inline-flex items-center gap-1.5 ${
                  activeTab === "ansprechpartner" ? "border-gold text-gold font-medium" : "border-transparent text-navy/55 hover:text-navy"
                }`}
              >
                <User size={12}/> Ansprechpartner
              </button>
              <button
                onClick={() => setActiveTab("kontakthistorie")}
                data-testid="drawer-tab-kontakthistorie"
                className={`text-xs uppercase tracking-widish font-sans px-4 py-2 border-b-2 transition-colors duration-150 inline-flex items-center gap-1.5 ${
                  activeTab === "kontakthistorie" ? "border-gold text-gold font-medium" : "border-transparent text-navy/55 hover:text-navy"
                }`}
              >
                <MessageSquare size={12}/> Kontakthistorie
              </button>
              <button
                onClick={() => setActiveTab("verlauf")}
                data-testid="drawer-tab-verlauf"
                className={`text-xs uppercase tracking-widish font-sans px-4 py-2 border-b-2 transition-colors duration-150 inline-flex items-center gap-1.5 ${
                  activeTab === "verlauf" ? "border-gold text-gold font-medium" : "border-transparent text-navy/55 hover:text-navy"
                }`}
              >
                <ActivityIcon size={12}/> Verlauf
              </button>
            </div>

            {activeTab === "docs" ? (
              <DocsTab data={data} onUpdate={(d) => { setData(d); setOrig(d); onChange?.(); }} />
            ) : activeTab === "dr-freigabe" ? (
              <DatenraumFreigabeTab investorId={data.id} firma={data.firma} />
            ) : activeTab === "expose-link" ? (
              <ExposeLinkTab
                investorId={data.id}
                firma={data.firma}
                anrede={data.ansprechpartner?.[0]?.anrede}
                nachname={data.ansprechpartner?.[0]?.nachname}
                bevorzugteSprache={data.bevorzugte_sprache || "de"}
              />
            ) : activeTab === "ansprechpartner" ? (
              <AnsprechpartnerTab investorId={data.id} investorFirma={data.firma} onChange={onChange} />
            ) : activeTab === "kontakthistorie" ? (
              <KontakthistorieTab investorId={data.id} onChange={onChange} />
            ) : activeTab === "verlauf" ? (
              <VerlaufTab investorId={data.id} />
            ) : (
              <>
            {/* Bidirektionale Sperre */}
            <SperreSection
              data={data}
              busyKey={busyKey}
              onToggle={toggleSperre}
              onChangeGrund={setGrundInput}
              grundInput={grundInput}
              showGrundFor={showGrundFor}
            />

            {/* Phase 4.5 — Match-Profil */}
            <section data-testid="drawer-match-profil">
              <div className="pb-eyebrow mb-3 flex items-center gap-2">
                Match-Profil
                <span className="text-[10px] tracking-wider2 text-navy/40 normal-case font-normal">
                  Heinrichs' Tier-Klassifikation & Schlüssel-Indikatoren
                </span>
              </div>
              <div className="pb-card">
                <div className="flex items-start gap-5 flex-wrap">
                  <div>
                    <div className="pb-label mb-2">Tier</div>
                    {!editMode ? (
                      <div className="flex items-center gap-2">
                        <TierBadge tier={data.tier} testid="drawer-tier-badge" />
                        <span className="text-xs text-navy/55 font-sans">
                          {data.tier == null
                            ? "noch nicht klassifiziert"
                            : data.tier === 1 ? "Perfekter Match"
                            : data.tier === 2 ? "Stark"
                            : "Weiter zu prüfen"}
                        </span>
                      </div>
                    ) : (
                      <select
                        data-testid="drawer-tier-select"
                        className="pb-input--full text-sm"
                        value={data.tier ?? ""}
                        onChange={(e) => set("tier")(e.target.value === "" ? null : Number(e.target.value))}
                      >
                        <option value="">— ohne Tier —</option>
                        {[1, 2, 3, 4, 5, 6].map((t) => (
                          <option key={t} value={t}>T{t}{t === 1 ? " (Perfekt-Match)" : t === 2 ? " (Stark)" : ""}</option>
                        ))}
                      </select>
                    )}
                  </div>

                  <div className="border-l border-navy/10 pl-5">
                    <div className="pb-label mb-2">Affinitäten</div>
                    {!editMode ? (
                      <div className="flex items-center gap-4 mt-2">
                        <AffinitaetDot value={data.ebr_affinitaet} label="EBR" testid="drawer-aff-ebr" />
                        <AffinitaetDot value={data.ls_affinitaet} label="LS" testid="drawer-aff-ls" />
                        <AffinitaetDot value={data.de_affinitaet} label="DE" testid="drawer-aff-de" />
                        {!data.ebr_affinitaet && !data.ls_affinitaet && !data.de_affinitaet && (
                          <span className="text-xs text-navy/40 font-sans">noch keine Affinitäten erfasst</span>
                        )}
                      </div>
                    ) : (
                      <div className="grid grid-cols-3 gap-2 mt-1">
                        {[
                          ["ebr_affinitaet", "EBR-Affinität"],
                          ["ls_affinitaet", "Life-Science-Affinität"],
                          ["de_affinitaet", "Deutschland-Affinität"],
                        ].map(([field, label]) => (
                          <div key={field}>
                            <label className="text-[10px] uppercase tracking-widish text-navy/55 block mb-1">{label}</label>
                            <select
                              data-testid={`drawer-${field}-select`}
                              className="pb-input--full text-xs"
                              value={data[field] || ""}
                              onChange={(e) => set(field)(e.target.value || null)}
                            >
                              <option value="">—</option>
                              <option value="hoch">Hoch</option>
                              <option value="mittel">Mittel</option>
                              <option value="niedrig">Niedrig</option>
                            </select>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {(data.schluessel_argument || editMode) && (
                  <div className="mt-5 pt-4 border-t border-navy/10">
                    <div className="pb-label mb-2">Schlüssel-Argument</div>
                    {!editMode ? (
                      <p
                        data-testid="drawer-schluessel-argument"
                        className="text-sm font-sans text-navy/85 leading-relaxed bg-ledger/50 border-l-2 border-gold pl-3 py-1"
                      >
                        {data.schluessel_argument || <span className="text-navy/40 italic">— noch nicht erfasst —</span>}
                      </p>
                    ) : (
                      <textarea
                        data-testid="drawer-schluessel-argument-input"
                        className="pb-input--full text-sm leading-relaxed min-h-[80px]"
                        placeholder="Warum dieser Kandidat? Key-Selling-Point…"
                        value={data.schluessel_argument || ""}
                        onChange={(e) => set("schluessel_argument")(e.target.value)}
                      />
                    )}
                  </div>
                )}
              </div>
            </section>

            {/* Pipeline & Betreuer */}
            <section>
              <div className="pb-eyebrow mb-3">Zuordnung &amp; Pipeline</div>
              <div className="grid grid-cols-2 gap-x-5 gap-y-4 text-sm font-sans">
                <div>
                  <label className="pb-label">Betreuer (intern)</label>
                  {!editMode ? (
                    <div className="mt-1.5 flex items-center gap-2">
                      <select
                        data-testid="drawer-betreuer-select-quick"
                        className="pb-input--full text-xs"
                        value={data.betreuer_intern || "Heinrichs"}
                        disabled={busyKey === "betreuer_intern"}
                        onChange={(e) => quickUpdate("betreuer_intern", e.target.value)}
                      >
                        {BETREUER.map((b) => <option key={b}>{b}</option>)}
                      </select>
                    </div>
                  ) : (
                    <select data-testid="drawer-betreuer-select" className="pb-input--full mt-1"
                      value={data.betreuer_intern || "Heinrichs"} onChange={(e) => set("betreuer_intern")(e.target.value)}>
                      {BETREUER.map((b) => <option key={b}>{b}</option>)}
                    </select>
                  )}
                </div>
                <div>
                  <label className="pb-label">Phase</label>
                  <select data-testid="drawer-phase-select" className="pb-input--full mt-1"
                    value={data.phase || "Identifiziert"}
                    disabled={!editMode && busyKey === "phase"}
                    onChange={(e) => editMode ? set("phase")(e.target.value) : quickUpdate("phase", e.target.value)}>
                    {PHASES.map((p) => <option key={p}>{p}</option>)}
                  </select>
                </div>
                <div>
                  <label className="pb-label">BNP-Sub-Code</label>
                  <select data-testid="drawer-bnpcode-select" className="pb-input--full mt-1"
                    value={data.bnp_sub_code || ""}
                    disabled={!editMode && busyKey === "bnp_sub_code"}
                    onChange={(e) => editMode ? set("bnp_sub_code")(e.target.value) : quickUpdate("bnp_sub_code", e.target.value)}>
                    {BNP_SUB_CODES.map((c) => <option key={c} value={c}>{c || "—"}</option>)}
                  </select>
                </div>
                <div>
                  <label className="pb-label">NDA-Status</label>
                  {!editMode ? (
                    <div className="mt-1 text-navy capitalize">{data.nda_status || "none"}</div>
                  ) : (
                    <select data-testid="drawer-nda-select" className="pb-input--full mt-1"
                      value={data.nda_status || "none"} onChange={(e) => set("nda_status")(e.target.value)}>
                      <option value="none">none</option>
                      <option value="versandt">versandt</option>
                      <option value="unterzeichnet">unterzeichnet</option>
                    </select>
                  )}
                </div>
                <div>
                  <label className="pb-label">NDA seit</label>
                  <div className="mt-1 text-navy">{fmtDate(data.nda_unterzeichnet_am)}</div>
                </div>
                {data.phase === "Abgesagt" && (
                  <div className="col-span-2">
                    <label className="pb-label">Absage-Grund</label>
                    {!editMode ? (
                      <div className="mt-1 text-navy">{data.absage_grund || "—"}</div>
                    ) : (
                      <select data-testid="drawer-absage-grund-select" className="pb-input--full mt-1"
                        value={data.absage_grund || ""} onChange={(e) => set("absage_grund")(e.target.value)}>
                        {ABSAGE_GRUENDE.map((g) => <option key={g} value={g}>{g || "—"}</option>)}
                      </select>
                    )}
                  </div>
                )}
              </div>
            </section>

            {/* Kontakt */}
            <section>
              <div className="pb-eyebrow mb-3">Kontakt</div>
              <div className="grid grid-cols-2 gap-x-5 gap-y-3 text-sm font-sans">
                {!editMode ? (
                  <>
                    <div className="min-w-0">
                      <div className="pb-label">E-Mail</div>
                      {data.email ? (
                        <a className="text-navy hover:text-gold inline-flex gap-1.5 items-center break-all" href={`mailto:${data.email}`}>
                          <Mail size={12} className="shrink-0"/>{data.email}
                        </a>
                      ) : <span className="text-navy/40">—</span>}
                    </div>
                    <div className="min-w-0">
                      <div className="pb-label">Telefon</div>
                      {data.telefon ? (
                        <a className="text-navy hover:text-gold inline-flex gap-1.5 items-center break-all" href={`tel:${(data.telefon||"").replace(/\s/g,"")}`}>
                          <Phone size={12} className="shrink-0"/>{data.telefon}
                        </a>
                      ) : <span className="text-navy/40">—</span>}
                    </div>
                    <div className="min-w-0">
                      <div className="pb-label">URL</div>
                      {data.url ? (
                        <a className="text-navy hover:text-gold inline-flex gap-1.5 items-center break-all" href={data.url} target="_blank" rel="noreferrer">
                          <ExternalLink size={12} className="shrink-0"/>{data.url.replace(/^https?:\/\//,"")}
                        </a>
                      ) : <span className="text-navy/40">—</span>}
                    </div>
                    <div className="min-w-0">
                      <div className="pb-label">Anschrift</div>
                      <div className="text-navy">{data.adresse || "—"}</div>
                    </div>
                    {data.geschaeftsfuehrung_raw && (
                      <div className="col-span-2">
                        <div className="pb-label">Geschäftsführung</div>
                        <div className="text-navy">{data.geschaeftsfuehrung_raw}</div>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <div><label className="pb-label">E-Mail</label><input data-testid="drawer-email-input" className="pb-input--full" value={data.email||""} onChange={(e)=>set("email")(e.target.value)}/></div>
                    <div><label className="pb-label">Telefon</label><input data-testid="drawer-telefon-input" className="pb-input--full" value={data.telefon||""} onChange={(e)=>set("telefon")(e.target.value)}/></div>
                    <div className="col-span-2"><label className="pb-label">URL</label><input className="pb-input--full" value={data.url||""} onChange={(e)=>set("url")(e.target.value)}/></div>
                    <div className="col-span-2"><label className="pb-label">Anschrift</label><input className="pb-input--full" value={data.adresse||""} onChange={(e)=>set("adresse")(e.target.value)}/></div>
                    <div><label className="pb-label">PLZ</label><input className="pb-input--full" value={data.plz||""} onChange={(e)=>set("plz")(e.target.value)}/></div>
                    <div><label className="pb-label">Stadt</label><input className="pb-input--full" value={data.stadt||""} onChange={(e)=>set("stadt")(e.target.value)}/></div>
                    <div className="col-span-2"><label className="pb-label">Geschäftsführung</label><input className="pb-input--full" value={data.geschaeftsfuehrung_raw||""} onChange={(e)=>set("geschaeftsfuehrung_raw")(e.target.value)}/></div>
                  </>
                )}
              </div>
            </section>

            {/* Profil */}
            <section>
              <div className="pb-eyebrow mb-3">Profil &amp; Fokus</div>
              <div className="grid grid-cols-2 gap-x-5 gap-y-3 text-sm font-sans">
                <div>
                  <div className="pb-label">AuM</div>
                  <div className="text-navy">{data.aum_text || (data.aum_mio_eur ? `${data.aum_mio_eur} Mio €` : "—")}</div>
                </div>
                <div>
                  <div className="pb-label">Fokus-Kategorien</div>
                  <div className="flex flex-wrap gap-1.5 mt-0.5">
                    {(data.fokus_kategorien || []).length === 0 && <span className="text-navy/40">—</span>}
                    {(data.fokus_kategorien || []).map((f) => (
                      <span key={f} className="text-[10px] uppercase tracking-widish px-2 py-0.5 bg-ledger text-navy/80 border border-navy/15">{f}</span>
                    ))}
                  </div>
                </div>
                {data.fokus_immobilien_details && (
                  <div className="col-span-2">
                    <div className="pb-label">Immobilien-Fokus Details</div>
                    <div className="text-navy">{data.fokus_immobilien_details}</div>
                  </div>
                )}
                {(data.fokus_sonstiges || data.kommentar) && (
                  <div className="col-span-2">
                    {data.fokus_sonstiges && (
                      <>
                        <div className="pb-label">Sonstiger Fokus</div>
                        <div className="text-navy">{data.fokus_sonstiges}</div>
                      </>
                    )}
                    {data.kommentar && (
                      <div className="mt-2">
                        <div className="pb-label">Kommentar</div>
                        {!editMode ? (
                          <div className="text-navy whitespace-pre-wrap">{data.kommentar}</div>
                        ) : (
                          <textarea className="pb-input--full" rows={3} value={data.kommentar||""} onChange={(e)=>set("kommentar")(e.target.value)} />
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </section>

            <section>
              <div className="pb-eyebrow mb-3 text-navy/55">Tipp</div>
              <div className="text-xs font-sans text-navy/55 border border-navy/10 px-4 py-3">
                Detaillierte Kontakthistorie und Auto-Einträge findest du im Tab&nbsp;
                <button
                  type="button"
                  onClick={() => setActiveTab("kontakthistorie")}
                  className="text-gold hover:underline"
                >
                  „Kontakthistorie"
                </button>.
              </div>
            </section>

            <div className="pt-3 border-t border-navy/10">
              <button data-testid="drawer-delete-btn" onClick={handleDelete} className="text-sm text-red-700 hover:text-red-900 font-sans inline-flex items-center gap-2">
                <Trash2 size={13}/> Investor löschen
              </button>
            </div>
              </>
            )}
          </div>
        )}
      </aside>

      {/* Mailto-Preview Modal (Direkt-Ansprache, etc.) */}
      {mailtoPreview && (
        <>
          <div onClick={() => setMailtoPreview(null)} className="fixed inset-0 bg-navy/40 z-[60]" />
          <div
            data-testid="drawer-mailto-modal"
            className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[min(720px,95vw)] max-h-[85vh] bg-white border border-navy/15 z-[70] flex flex-col"
          >
            <div className="border-b border-navy/10 px-6 py-4 flex items-center justify-between">
              <div>
                <div className="pb-eyebrow">
                  {mailtoPreview.typ === "direkt_ansprache" ? "Direkt-Ansprache" : mailtoPreview.typ}
                </div>
                <h3 className="font-display font-semibold text-navy text-xl mt-1 tracking-tight">
                  {mailtoPreview.preview?.subject || "Mail vorbereiten"}
                </h3>
              </div>
              <button data-testid="mailto-preview-close" onClick={() => setMailtoPreview(null)} aria-label="Vorschau schließen" className="text-navy/60 hover:text-navy min-w-[44px] min-h-[44px] -mr-2.5 inline-flex items-center justify-center"><X size={18}/></button>
            </div>
            <div className="px-6 py-5 flex-1 overflow-y-auto space-y-3 text-sm font-sans">
              <div>
                <div className="pb-label">An</div>
                <div className="text-navy mt-1 break-all">{mailtoPreview.preview?.to || <span className="text-navy/45">— keine E-Mail-Adresse hinterlegt —</span>}</div>
              </div>
              <div>
                <div className="pb-label">Nachricht</div>
                <pre data-testid="drawer-mailto-body" className="text-navy mt-1 whitespace-pre-wrap font-sans bg-ledger/40 border border-navy/10 p-4 text-[13px] leading-relaxed">
                  {mailtoPreview.preview?.body || ""}
                </pre>
              </div>
            </div>
            <div className="border-t border-navy/10 px-6 py-4 flex justify-end gap-2">
              <button onClick={() => setMailtoPreview(null)} className="pb-btn-ghost">Schließen</button>
              <a
                data-testid="drawer-mailto-open-client"
                href={mailtoPreview.mailto_url}
                onClick={() => setTimeout(() => setMailtoPreview(null), 200)}
                className="pb-btn-accent gap-2"
              >
                <Mail size={14}/> Mail-Client öffnen
              </a>
            </div>
          </div>
        </>
      )}

      <style>{`@keyframes slide-in { from { transform: translateX(100%); } to { transform: translateX(0); } }`}</style>
    </>
  );
}


// ---------- Welle 8: Erweiterter Exposé-Link-Tab mit Variant/Sprache-Wahl ----------
function ExposeLinkTab({ investorId, firma, anrede, nachname, bevorzugteSprache = "de" }) {
  const [links, setLinks] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [creating, setCreating] = React.useState(null);       // "teaser-de" | "teaser-en" | "voll-de" | "voll-en" | null
  const [archiveTarget, setArchiveTarget] = React.useState(null);
  const [archiveNote, setArchiveNote] = React.useState("");

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const d = await listExposeLinks({ investor_id: investorId });
      setLinks(d.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Konnte Exposé-Links nicht laden");
    } finally {
      setLoading(false);
    }
  }, [investorId]);

  React.useEffect(() => { load(); }, [load]);

  const doCreate = async (variant, language) => {
    const key = `${variant === "teaser" ? "teaser" : "voll"}-${language}`;
    setCreating(key);
    try {
      const link = await createExposeLink({
        link_type: "investor",
        investor_id: investorId,
        variant,
        language,
      });
      try {
        await navigator.clipboard.writeText(exposePublicUrl(link.token));
        toast.success(`${variant === "teaser" ? "Teaser" : "Vollversion"} (${language.toUpperCase()}) erzeugt und kopiert`);
      } catch {
        toast.success(`${variant === "teaser" ? "Teaser" : "Vollversion"} (${language.toUpperCase()}) erzeugt`);
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Konnte Link nicht erzeugen");
    } finally {
      setCreating(null);
    }
  };

  const doRevoke = async (link) => {
    if (!window.confirm("Exposé-Link wirklich widerrufen? Empfänger sehen dann sofort eine Fehlerseite.")) return;
    try {
      await deleteExposeLink(link.id);
      toast.success("Widerrufen");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Widerruf fehlgeschlagen");
    }
  };

  const openArchive = (link) => {
    setArchiveTarget(link);
    setArchiveNote("");
  };
  const submitArchive = async () => {
    if (!archiveTarget) return;
    try {
      await archiveExposeLink(archiveTarget.id, archiveNote.trim());
      toast.success("Link archiviert");
      setArchiveTarget(null);
      setArchiveNote("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Archivieren fehlgeschlagen");
    }
  };

  const doCopy = async (token) => {
    try {
      await navigator.clipboard.writeText(exposePublicUrl(token));
      toast.success("Link kopiert");
    } catch {
      window.prompt("Link kopieren:", exposePublicUrl(token));
    }
  };

  const [pdfLoadingId, setPdfLoadingId] = React.useState(null);
  const doDownloadPdf = async (link) => {
    setPdfLoadingId(link.id);
    const tid = `drawer-pdf-${link.id}`;
    toast.loading(
      link.variant === "teaser" ? "PDF wird erstellt · ~5 Sek" : "PDF wird erstellt · ~10 Sek",
      { id: tid }
    );
    try {
      const meta = await generateExposePdf(link.id);
      const sizeMb = (meta.size_bytes / 1_000_000).toFixed(1);
      toast.success(
        meta.cached ? `PDF bereit (${sizeMb} MB · Cache)` : `PDF erstellt (${sizeMb} MB · ${meta.duration_s}s)`,
        { id: tid }
      );
      const a = document.createElement("a");
      a.href = exposePdfDownloadUrl(link.id);
      a.download = meta.filename || "Adldinger_Newton_Campus_Garching_Expose.pdf";
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "PDF-Erstellung fehlgeschlagen", { id: tid });
    } finally {
      setPdfLoadingId(null);
    }
  };

  const buildMailto = (link) => {
    const url = exposePublicUrl(link.token);
    const lang = link.language || "de";
    const variant = link.variant || "vollversion";
    if (lang === "en") {
      const salutation = anrede === "Herr"
        ? `Dear Mr. ${nachname || ""}`.trim().replace(/\s+$/, "")
        : anrede === "Frau"
        ? `Dear Ms. ${nachname || ""}`.trim().replace(/\s+$/, "")
        : "Dear Sir or Madam";
      const subject = encodeURIComponent("Newton Campus Garching — Investment Opportunity");
      const body = encodeURIComponent(
        `${salutation},\n\nplease find the ${variant === "teaser" ? "teaser" : "full exposé"} for our sale mandate Newton Campus Garching:\n\n${url}\n\nFor any questions, I remain at your disposal.\n\nKind regards`
      );
      return `mailto:?subject=${subject}&body=${body}`;
    }
    const anredeText = anrede === "Herr"
      ? `Sehr geehrter Herr ${nachname || ""}`.trim().replace(/\s+$/, "")
      : anrede === "Frau"
      ? `Sehr geehrte Frau ${nachname || ""}`.trim().replace(/\s+$/, "")
      : "Sehr geehrte Damen und Herren";
    const subject = encodeURIComponent("Newton Campus Garching — Exposé");
    const body = encodeURIComponent(
      `${anredeText},\n\nanbei ${variant === "teaser" ? "der Teaser" : "das Exposé"} zu unserem Verkaufsmandat Newton Campus Garching:\n\n${url}\n\nBei Rückfragen stehe ich gerne zur Verfügung.\n\nMit freundlichen Grüßen`
    );
    return `mailto:?subject=${subject}&body=${body}`;
  };

  // Buttons für die 4 Kombinationen — bevorzugte Sprache visuell hervorgehoben
  const pref = bevorzugteSprache === "en" ? "en" : "de";
  const btn = (variant, language) => {
    const key = `${variant === "teaser" ? "teaser" : "voll"}-${language}`;
    const isPref = language === pref;
    const isTeaser = variant === "teaser";
    return (
      <button
        key={key}
        data-testid={`drawer-expose-create-${key}`}
        onClick={() => doCreate(variant, language)}
        disabled={creating !== null}
        className={`text-left px-3 py-3 border transition-colors font-sans disabled:opacity-50 disabled:cursor-not-allowed ${
          isPref
            ? "bg-gold/8 border-gold/60 hover:bg-gold/15"
            : "bg-white border-navy/15 hover:bg-navy/[0.04]"
        }`}
      >
        <div className="flex items-center gap-1.5 mb-1">
          <span
            className={`text-[10px] uppercase tracking-widish font-semibold ${
              isTeaser ? "text-heiss" : "text-navy"
            }`}
          >
            {isTeaser ? "Teaser" : "Vollversion"}
          </span>
          <Globe size={9} className="text-navy/40" />
          <span className="text-[10px] uppercase tracking-widish text-navy/70 font-medium">
            {language.toUpperCase()}
          </span>
          {isPref && (
            <span className="text-[9px] uppercase tracking-wider text-gold/90 font-medium">
              · bevorzugt
            </span>
          )}
        </div>
        <div className="text-xs text-navy/70 leading-snug">
          {isTeaser
            ? (language === "en" ? "Cold outreach · no NDA required" : "Erstansprache · ohne NDA")
            : (language === "en" ? "Full document · after NDA" : "Vollständiges Dokument · nach NDA")}
        </div>
        <div className="mt-2 text-[11px] uppercase tracking-widish text-navy font-medium inline-flex items-center gap-1">
          {creating === key ? "Erzeugt…" : <><Plus size={11}/> Link erzeugen</>}
        </div>
      </button>
    );
  };

  return (
    <div data-testid="drawer-expose-tab" className="space-y-4">
      <div>
        <div className="pb-eyebrow">Vertriebsmaterial</div>
        <h4 className="font-display font-semibold text-navy text-base tracking-tight mt-1">
          Exposé-Link für {firma}
        </h4>
        <p className="text-xs text-navy/55 font-sans mt-0.5">
          Wählen Sie Variante und Sprache — die für diesen Investor hinterlegte
          <span className="text-gold font-medium"> bevorzugte Sprache ({pref.toUpperCase()})</span> ist markiert.
          Standard-Ablauf: 60 Tage. Widerruf oder Archivierung jederzeit möglich.
        </p>
      </div>

      {/* 4 Buttons: Teaser DE/EN · Voll DE/EN */}
      <div className="grid grid-cols-2 gap-2">
        {btn("teaser", "de")}
        {btn("teaser", "en")}
        {btn("vollversion", "de")}
        {btn("vollversion", "en")}
      </div>

      <div className="pt-1">
        <div className="pb-eyebrow mb-2">Bisher erzeugte Links</div>
        {loading && <div className="text-sm text-navy/55 font-sans">Lädt…</div>}
        {!loading && links.length === 0 && (
          <div className="border border-dashed border-navy/20 px-4 py-6 text-center text-sm text-navy/55 font-sans">
            Noch kein Exposé-Link erzeugt. Wählen Sie oben eine der 4 Varianten.
          </div>
        )}
        <ul className="divide-y divide-navy/8 border border-navy/12">
          {links.map((link) => {
            const isTeaser = link.variant === "teaser";
            const langCode = (link.language || "de").toUpperCase();
            return (
              <li key={link.id} className="px-3 py-3 flex items-center gap-3" data-testid="drawer-expose-row">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`inline-flex items-center px-2 py-0.5 text-[10px] uppercase tracking-wider font-sans border ${link.status === "aktiv" ? "bg-emerald-50 text-emerald-800 border-emerald-200" : link.status === "widerrufen" ? "bg-rose-50 text-rose-800 border-rose-200" : link.status === "archiviert" ? "bg-navy/8 text-navy border-navy/20" : "bg-gold/10 text-[#8B6914] border-gold/40"}`}>
                      {link.status}
                    </span>
                    <span
                      className={`inline-flex items-center px-1.5 py-0.5 text-[9px] uppercase tracking-wider font-sans border ${
                        isTeaser
                          ? "bg-heiss/10 text-heiss border-heiss/30"
                          : "bg-navy/8 text-navy border-navy/20"
                      }`}
                    >
                      {isTeaser ? "Teaser" : "Voll"}
                    </span>
                    <span className="text-[9px] uppercase tracking-wider text-navy/55 font-sans inline-flex items-center gap-0.5">
                      <Globe size={9}/>{langCode}
                    </span>
                    <span className="text-[11px] text-navy/55 font-sans font-mono truncate">
                      /expose/{link.token.slice(0, 10)}…
                    </span>
                  </div>
                  <div className="text-[11px] text-navy/55 font-sans mt-1">
                    <Eye size={10} className="inline mr-1" /> {link.access_count} Zugriff{link.access_count === 1 ? "" : "e"}
                    {link.last_access_at && ` · letzter: ${new Date(link.last_access_at).toLocaleString("de-DE")}`}
                    {link.expires_at && ` · läuft ab ${new Date(link.expires_at).toLocaleDateString("de-DE")}`}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => doCopy(link.token)}
                    title="Link kopieren"
                    data-testid="drawer-expose-copy"
                    className="p-1.5 text-navy/60 hover:text-navy hover:bg-navy/5 border border-navy/15"
                  >
                    <Copy size={12} />
                  </button>
                  <a
                    href={buildMailto(link)}
                    title={`Mail-Vorlage öffnen (${langCode})`}
                    data-testid="drawer-expose-mailto"
                    className="p-1.5 text-navy/60 hover:text-gold hover:bg-gold/10 border border-navy/15"
                  >
                    <Mail size={12} />
                  </a>
                  <a
                    href={exposePublicUrl(link.token)}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Vorschau"
                    className="p-1.5 text-navy/60 hover:text-navy hover:bg-navy/5 border border-navy/15"
                  >
                    <ExternalLink size={12} />
                  </a>
                  <button
                    onClick={() => doDownloadPdf(link)}
                    disabled={pdfLoadingId === link.id}
                    title="Als professionelles PDF herunterladen"
                    data-testid="drawer-expose-pdf"
                    className="p-1.5 text-navy/60 hover:text-gold hover:bg-gold/10 border border-navy/15 disabled:opacity-60 disabled:cursor-wait"
                  >
                    {pdfLoadingId === link.id ? <Loader2 size={12} className="animate-spin"/> : <FileDown size={12}/>}
                  </button>
                  {link.status === "aktiv" && (
                    <button
                      onClick={() => doRevoke(link)}
                      title="Widerrufen"
                      data-testid="drawer-expose-revoke"
                      className="p-1.5 text-tot/70 hover:text-white hover:bg-tot border border-tot/30"
                    >
                      <XCircle size={12} />
                    </button>
                  )}
                  <button
                    onClick={() => openArchive(link)}
                    title="Archivieren"
                    data-testid="drawer-expose-archive"
                    className="p-1.5 text-navy/60 hover:text-white hover:bg-navy border border-navy/30"
                  >
                    <Archive size={12} />
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      </div>

      {archiveTarget && (
        <>
          <div onClick={() => setArchiveTarget(null)} className="fixed inset-0 bg-navy/40 z-[60]" />
          <div
            className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[min(480px,95vw)] bg-white border border-navy/15 z-[70]"
            data-testid="drawer-expose-archive-modal"
          >
            <div className="border-b border-navy/10 px-6 py-4">
              <div className="pb-eyebrow">Link archivieren</div>
              <h3 className="font-display font-semibold text-navy text-lg mt-1 tracking-tight">
                {archiveTarget.variant === "teaser" ? "Teaser" : "Vollversion"} · {(archiveTarget.language || "de").toUpperCase()}
              </h3>
            </div>
            <div className="px-6 py-5 space-y-3 text-sm font-sans">
              <p className="text-navy/80 leading-relaxed">
                Der Link verschwindet aus der aktiven Liste und ist für Empfänger nicht mehr aufrufbar. Er kann jederzeit aus dem Archiv wiederhergestellt werden.
              </p>
              <div>
                <label className="pb-label">Notiz (optional)</label>
                <textarea
                  data-testid="drawer-expose-archive-note"
                  value={archiveNote}
                  onChange={(e) => setArchiveNote(e.target.value)}
                  placeholder="z. B. Empfänger hat abgesagt / NDA-Prozess beendet"
                  className="pb-input--full text-sm min-h-[64px]"
                  autoFocus
                />
              </div>
            </div>
            <div className="border-t border-navy/10 px-6 py-4 flex justify-end gap-2">
              <button onClick={() => setArchiveTarget(null)} className="pb-btn-ghost">Abbrechen</button>
              <button
                onClick={submitArchive}
                data-testid="drawer-expose-archive-submit"
                className="pb-btn-primary gap-2"
              >
                <Archive size={13}/> Archivieren
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}



/**
 * Default-Export: gleicher Drawer, aber gekapselt in ErrorBoundary.
 * Bei einem React-Render-Fehler (z. B. unerwartet null Feld) zeigt der Fallback
 * die Investor-ID + Fehlermeldung — Heinrichs kann diese 1:1 melden.
 */
export default function InvestorDrawer(props) {
  if (!props.investorId) return null;
  return (
    <ErrorBoundary
      contextLabel="Investor-Drawer konnte nicht angezeigt werden"
      contextId={props.investorId}
      onReset={props.onClose}
    >
      <InvestorDrawerInner {...props} />
    </ErrorBoundary>
  );
}
